// /Users/donovan68/dva-frontend-vite/src/pages/pro/ROIModelingPage.tsx
// Step 2: Analysis & Qualification - ROI Modeling
// Shows portfolio landing page at /pro/analysis
// Shows deep-dive analysis at /pro/analysis/:useCaseId
// UPDATED: Added ProJourneyPageHeader for consistent styling across all Step 2 pages
// FIX: Replaced hardcoded localhost URL with buildApiUrl

import React, { useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ChevronRight, AlertCircle, Loader2 } from 'lucide-react';
import { ProProgressPathV3 } from '../../components/ProProgressPathV3';
import ProJourneyPageHeader from '../../components/journey/pro/ProJourneyPageHeader';
import ProspectPanel from '../../components/ProspectPanel';
import { useClient } from '../../contexts/ClientContext';
import { useProspectList } from '../../hooks/useProspectList';
import { convertToProspectPanelFormat } from '../../utils/prospectUtils';
import { useFilter } from '../../contexts/FilterContext';
import { useUseCaseStatuses, type DecisionValue } from '../../hooks/useUseCaseStatuses';
import { cn } from '../../components/ui/utils';
import ROICommandCenter from '../../components/roi/ROICommandCenter';
import { mockScenarios } from '../../data/roiMockData';
import { buildApiUrl } from '../../config/appConfig';
import { fetchROIModel, formatCurrency, formatHeaderRoiPercent, formatPayback, formatPercent, type ROIModelResponse, type ScenarioId } from '../../services/roiService';

type ConfidenceLabel = 'Low' | 'Medium' | 'High';

type RoiSummary = {
  useCaseId: number;
  name: string;
  category: string;
  annualBenefit: number;
  net3yrValue: number;
  roiPercent: number;
  paybackMonths: number;
  confidenceLabel: ConfidenceLabel;
};

// Mock data for MVP - replace with API call later
const MOCK_ROI_SUMMARIES: RoiSummary[] = [
  {
    useCaseId: 77,
    name: 'Clinical Analytics Platform',
    category: 'Clinical Quality',
    annualBenefit: 1500000,
    net3yrValue: 4200000,
    roiPercent: 444,
    paybackMonths: 18,
    confidenceLabel: 'High',
  },
  {
    useCaseId: 42,
    name: 'Population Health Dashboard',
    category: 'Value-Based Care',
    annualBenefit: 1200000,
    net3yrValue: 3200000,
    roiPercent: 310,
    paybackMonths: 20,
    confidenceLabel: 'High',
  },
  {
    useCaseId: 56,
    name: 'Predictive Care Models',
    category: 'Value-Based Care',
    annualBenefit: 900000,
    net3yrValue: 2400000,
    roiPercent: 280,
    paybackMonths: 22,
    confidenceLabel: 'Medium',
  },
  {
    useCaseId: 34,
    name: 'Revenue Cycle Optimization',
    category: 'Financial Operations',
    annualBenefit: 850000,
    net3yrValue: 2100000,
    roiPercent: 265,
    paybackMonths: 24,
    confidenceLabel: 'High',
  },
  {
    useCaseId: 51,
    name: 'Predictive Staffing Models',
    category: 'Operational Excellence',
    annualBenefit: 720000,
    net3yrValue: 1900000,
    roiPercent: 240,
    paybackMonths: 26,
    confidenceLabel: 'Medium',
  },
];

const formatCurrencyShort = (value: number): string => {
  if (!Number.isFinite(value)) return '$0';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const formatMillions = (value: number): string => {
  if (!Number.isFinite(value)) return '$0';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
};

const getConfidenceBadgeColor = (confidence: ConfidenceLabel): string => {
  switch (confidence) {
    case 'High':
      return 'bg-green-100 text-green-700';
    case 'Medium':
      return 'bg-amber-100 text-amber-700';
    case 'Low':
      return 'bg-slate-100 text-slate-600';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

const ROIModelingPage: React.FC = () => {
  const { useCaseId } = useParams<{ useCaseId: string }>();
  const { clientId } = useClient();
  const roiClientId = 'tenant-a';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { prospects: rawProspects, updateStatus } = useProspectList(clientId || '');
  const prospects = convertToProspectPanelFormat(rawProspects);
  const { activeFilter, setActiveFilter } = useFilter();
  const messageCount = 4;

  // ROI data state
  const [roiData, setRoiData] = useState<ROIModelResponse | null>(null);
  const [roiLoading, setRoiLoading] = useState<boolean>(false);
  const [roiError, setRoiError] = useState<string | null>(null);
  const activeScenario = (searchParams.get('scenario') as ScenarioId) || 'base';

  // Status and decision management
  const { statusByUseCaseId, updateStatus: updateUseCaseStatus } = useUseCaseStatuses(clientId || null);
  const [selectedUseCaseId, setSelectedUseCaseId] = useState<number | null>(null);

  // State for use case details (used in deep-dive view)
  const [useCaseDetails, setUseCaseDetails] = useState<{
    name: string;
    category_name?: string;
  } | null>(null);

  const DECISION_OPTIONS: DecisionValue[] = ['undecided', 'proceed', 'refine', 'monitor', 'defer', 'reject'];

  // Fetch use case details when in deep-dive view
  useEffect(() => {
    const fetchUseCaseDetails = async () => {
      if (!useCaseId || !clientId) {
        setUseCaseDetails(null);
        return;
      }

      const id = Number(useCaseId);
      try {
        // FIX: Use buildApiUrl instead of hardcoded localhost
        const response = await fetch(buildApiUrl(`/api/v2/useCases/${id}?client_id=${clientId}`));
        if (response.ok) {
          const data = await response.json();
          setUseCaseDetails({
            name: data.name || data.title || `Use Case #${id}`,
            category_name: data.category_name || data.category,
          });
        } else {
          // Fallback to mock data
          const mockUseCase = MOCK_ROI_SUMMARIES.find(uc => uc.useCaseId === id);
          if (mockUseCase) {
            setUseCaseDetails({
              name: mockUseCase.name,
              category_name: mockUseCase.category,
            });
          } else {
            setUseCaseDetails({
              name: `Use Case #${id}`,
            });
          }
        }
      } catch (err) {
        console.error('Failed to fetch use case details:', err);
        // Fallback to mock data
        const mockUseCase = MOCK_ROI_SUMMARIES.find(uc => uc.useCaseId === id);
        if (mockUseCase) {
          setUseCaseDetails({
            name: mockUseCase.name,
            category_name: mockUseCase.category,
          });
        } else {
          setUseCaseDetails({
            name: `Use Case #${id}`,
          });
        }
      }
    };
    fetchUseCaseDetails();
  }, [useCaseId, clientId]);

  // Fetch ROI Model data when in deep-dive view
  useEffect(() => {
    const fetchROIData = async () => {
      if (!useCaseId) {
        setRoiData(null);
        setRoiError(null);
        return;
      }

      setRoiLoading(true);
      setRoiError(null);

      try {
        const data = await fetchROIModel(roiClientId, useCaseId);
        setRoiData(data);
      } catch (err) {
        console.error('Failed to fetch ROI model:', err);
        setRoiError(err instanceof Error ? err.message : 'Failed to load ROI data');
        setRoiData(null);
      } finally {
        setRoiLoading(false);
      }
    };

    fetchROIData();
  }, [useCaseId, roiClientId]);

  const handleScenarioChange = (scenario: ScenarioId) => {
    setSearchParams({ scenario });
  };

  // Handlers for ProspectPanel
  const handleProspectRemove = async (prospectId: string) => {
    if (!clientId) return;
    try {
      await updateStatus(Number(prospectId), 'qualified');
    } catch (err) {
      console.error('Failed to remove prospect:', err);
    }
  };

  const handleProfileClick = (prospectId: string) => {
    navigate(`/usecase/${prospectId}`);
  };

  const handleRoiClick = (prospectId: string) => {
    navigate(`/pro/analysis/${prospectId}`);
  };

  const handlePlanClick = (prospectId: string) => {
    navigate(`/pro/roadmap-planning`);
  };

  const handleReorder = (reordered: any[]) => {
    console.log('Prospects reordered:', reordered);
  };

  // ========================================
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // ========================================

  // Portfolio calculations (always calculate, even if not displayed)
  const sortedSummaries = useMemo(
    () =>
      [...MOCK_ROI_SUMMARIES].sort(
        (a, b) => b.net3yrValue - a.net3yrValue
      ),
    []
  );

  const portfolioTotals = useMemo(() => {
    if (sortedSummaries.length === 0) {
      return {
        totalNet3yr: 0,
        totalAnnual: 0,
        avgPayback: 0,
        avgConfidence: 'N/A' as string,
      };
    }

    const totalNet3yr = sortedSummaries.reduce(
      (sum, uc) => sum + uc.net3yrValue,
      0
    );
    const totalAnnual = sortedSummaries.reduce(
      (sum, uc) => sum + uc.annualBenefit,
      0
    );
    const avgPayback =
      sortedSummaries.reduce((sum, uc) => sum + uc.paybackMonths, 0) /
      sortedSummaries.length;

    const confidenceCounts: Record<ConfidenceLabel, number> = {
      Low: 0,
      Medium: 0,
      High: 0,
    };
    sortedSummaries.forEach((uc) => {
      confidenceCounts[uc.confidenceLabel] += 1;
    });
    const topConfidence =
      Object.entries(confidenceCounts).sort((a, b) => b[1] - a[1])[0]?.[0] ??
      'N/A';

    return {
      totalNet3yr,
      totalAnnual,
      avgPayback,
      avgConfidence: topConfidence,
    };
  }, [sortedSummaries]);

  // ========================================
  // NAVIGATION HANDLERS (3-STEP SYSTEM)
  // ========================================

  const handlePhaseClick = (phaseId: string) => {
    switch (phaseId) {
      case 'portfolio':
        navigate('/home');
        break;
      case 'analysis':
        navigate('/pro/analysis'); // Stay on this page
        break;
      case 'approval_execution':
        navigate('/pro/approval');
        break;
      default:
        navigate('/home');
    }
  };

  const handleDeepDive = (targetUseCaseId: number) => {
    navigate(`/pro/analysis/${targetUseCaseId}`);
  };

  const handleDecisionChange = async (useCaseId: number, decision: DecisionValue) => {
    try {
      await updateUseCaseStatus(useCaseId, { decision });
    } catch (err) {
      console.error('Failed to update decision:', err);
      // Optionally show toast/error message
    }
  };

  const handleSendToApproval = async (useCaseId: number) => {
    try {
      await updateUseCaseStatus(useCaseId, { status: 'approval' });
      // Optionally show success message
    } catch (err) {
      console.error('Failed to send to approval:', err);
    }
  };

  // ========================================
  // CONDITIONAL RENDERING (AFTER ALL HOOKS)
  // ========================================

  // DEEP-DIVE VIEW (when useCaseId is present)
  if (useCaseId) {
    const id = Number(useCaseId);
    const currentStatus = statusByUseCaseId.get(id);
    const canSendToApproval = currentStatus?.decision === 'proceed';

    const useCaseName = useCaseDetails?.name || MOCK_ROI_SUMMARIES.find(
      (uc) => uc.useCaseId === id
    )?.name || `Use Case #${id}`;

    const showRoiLoader =
      !roiError && (roiLoading || (!!useCaseId && roiData == null));

    return (
      <main className="max-w-[1440px] mx-auto px-6 pb-4">
        {/* LAYOUT ORDER: Tabs → Page Header → Journey Bar → Content */}

        {/* 0. Content Tabs (Tools / Documents / Messages) */}
        <div className="h-10 flex items-center justify-end gap-2 pt-3 mb-0.5">
          <nav className="flex items-center gap-2" aria-label="Section navigation">
            {['Tools', 'Documents', 'Messages'].map((tab) => {
              const active = tab === activeFilter;
              const isMessages = tab === 'Messages';
              const showBadge = isMessages && messageCount > 0;

              return (
                <button
                  key={tab}
                  onClick={() => setActiveFilter(tab)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative
                    focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18365E]
                    ${active ? "bg-[#18365E] text-white" : "text-[#18365E] hover:bg-black/5"}`}
                  aria-current={active ? "page" : undefined}
                >
                  {tab}
                  {showBadge && (
                    <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6E4C] text-[10px] font-bold text-white">
                      {messageCount}
                    </span>
                  )}
                </button>
              );
            })}
          </nav>
        </div>

        {/* 1. Page Header - ROI Command Center */}
        <div className="mb-6 -mt-2">
          <ProJourneyPageHeader
            stepNumber={2}
            stepName="ROI Command Center"
            description="Validate assumptions, model scenarios, run sensitivity analysis, and finalize the ROI story."
          />
        </div>

        {/* 2. Journey Bar */}
        <div className="mb-4">
          <ProProgressPathV3
            currentPhase="analysis"
            onPhaseClick={handlePhaseClick}
          />
        </div>

        {/* 3. Main content with fixed right panel */}
        <div className="flex gap-0">
          {/* LEFT: main content */}
          <div className="flex-1 min-w-0">
            {roiError ? (
              <div className="bg-white rounded-xl border border-red-200 p-4 mb-4">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-semibold text-red-900 mb-1">Failed to load ROI model</h3>
                    <p className="text-sm text-red-700">{roiError}</p>
                    <button
                      onClick={() => {
                        if (useCaseId) {
                          setRoiLoading(true);
                          fetchROIModel(roiClientId, useCaseId)
                            .then(setRoiData)
                            .catch(err => setRoiError(err instanceof Error ? err.message : 'Unknown error'))
                            .finally(() => setRoiLoading(false));
                        }
                      }}
                      className="mt-2 text-sm font-medium text-red-700 hover:text-red-900 underline"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              </div>
            ) : showRoiLoader ? (
              <div className="bg-white rounded-xl border border-slate-200 p-12 mb-4 flex flex-col items-center justify-center min-h-[520px] gap-4">
                <Loader2 className="w-10 h-10 text-[#18365E] animate-spin" aria-hidden />
                <p className="text-sm font-semibold text-slate-800">Loading ROI model…</p>
                <p className="text-xs text-slate-500 text-center max-w-md">
                  Fetching scenarios, assumptions lab, sensitivity, and financial breakdown. This may take a few seconds.
                </p>
              </div>
            ) : roiData ? (
              <>
                <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h2 className="text-lg font-semibold text-slate-900">
                        {useCaseDetails?.name || `Use Case #${id}`}
                      </h2>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        className="text-sm border border-slate-200 rounded-lg px-3 py-2 bg-white hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-[#18365E] focus:border-[#18365E]"
                        value={currentStatus?.decision || 'undecided'}
                        onChange={(e) => {
                          updateUseCaseStatus(id, { decision: e.target.value as DecisionValue });
                        }}
                      >
                        {DECISION_OPTIONS.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt === 'undecided' ? 'Undecided' : opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </option>
                        ))}
                      </select>

                      <button
                        onClick={() => {
                          updateUseCaseStatus(id, { status: 'approval', decision: 'proceed' }).then(() => {
                            navigate(`/pro/approval?useCaseId=${id}`);
                          });
                        }}
                        disabled={!canSendToApproval}
                        className={cn(
                          "inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors",
                          canSendToApproval
                            ? "bg-[#18365E] text-white hover:bg-[#132845]"
                            : "bg-slate-100 text-slate-400 cursor-not-allowed"
                        )}
                      >
                        Next: Approval & Execution
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center gap-6 pt-3 border-t border-slate-100">
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">ROI (Base)</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {formatHeaderRoiPercent(roiData.data.headerMetrics.roiBase)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Payback</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {formatPayback(roiData.data.headerMetrics.paybackMonthsBase)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Net 3-Year Value</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {formatCurrency(roiData.data.headerMetrics.net3YearValueBase)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase tracking-wide">Confidence</p>
                      <p className="text-lg font-semibold text-slate-900">
                        {roiData.data.headerMetrics.confidenceBase}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  <ROICommandCenter
                    useCaseName={useCaseName}
                    roiData={roiData}
                    activeScenario={activeScenario}
                    onScenarioChange={handleScenarioChange}
                    onExportOnePager={() => {
                      console.log('Export One-Pager clicked');
                    }}
                    onExportBoardDeck={() => {
                      console.log('Export Board Deck clicked');
                    }}
                  />
                </div>
              </>
            ) : null}

            {/* Navigation Buttons */}
            <div className="mt-8 flex items-center justify-between">
              <button
                onClick={() => navigate('/pro/analysis')}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg border-2 border-slate-300 text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Portfolio
              </button>

              <button
                onClick={() => navigate('/pro/approval')}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Next: Approval & Execution
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* RIGHT: Prospect Panel - Fixed */}
          {clientId && (
            <div className="hidden lg:block">
              <ProspectPanel
                prospects={prospects}
                onRemove={handleProspectRemove}
                onProfileClick={handleProfileClick}
                onRoiClick={handleRoiClick}
                onPlanClick={handlePlanClick}
                onReorder={handleReorder}
              />
            </div>
          )}
        </div>
      </main>
    );
  }

  // ========================================
  // PORTFOLIO LANDING VIEW (when no useCaseId)
  // ========================================

  return (
    <main className="max-w-[1440px] mx-auto px-6 pb-4">
      {/* LAYOUT ORDER: Tabs → Page Header → Journey Bar → Content */}

      {/* 0. Content Tabs (Tools / Documents / Messages) */}
      <div className="h-10 flex items-center justify-end gap-2 pt-3 mb-0.5">
        <nav className="flex items-center gap-2" aria-label="Section navigation">
          {['Tools', 'Documents', 'Messages'].map((tab) => {
            const active = tab === activeFilter;
            const isMessages = tab === 'Messages';
            const showBadge = isMessages && messageCount > 0;

            return (
              <button
                key={tab}
                onClick={() => setActiveFilter(tab)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors relative
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#18365E]
                  ${active ? "bg-[#18365E] text-white" : "text-[#18365E] hover:bg-black/5"}`}
                aria-current={active ? "page" : undefined}
              >
                {tab}
                {showBadge && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-[#FF6E4C] text-[10px] font-bold text-white">
                    {messageCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* 1. Page Header - Analysis & Qualification */}
      <div className="mb-6 -mt-2">
        <ProJourneyPageHeader
          stepNumber={2}
          stepName="Analysis & Qualification"
          description="Model the financial impact of your opportunities with CFO-grade ROI analysis, payback projections, and portfolio confidence scoring."
        />
      </div>

      {/* 2. Journey Bar */}
      <div className="mb-6">
        <ProProgressPathV3
          currentPhase="analysis"
          onPhaseClick={handlePhaseClick}
        />
      </div>

      {/* 3. Main content with fixed right panel */}
      <div className="flex gap-0">
        {/* LEFT: main content */}
        <div className="flex-1 min-w-0">
          {/* Preview Panel - Shows when a use case is selected */}
          {selectedUseCaseId && (() => {
            const selectedUseCase = sortedSummaries.find(uc => uc.useCaseId === selectedUseCaseId);
            if (!selectedUseCase) return null;

            return (
              <section className="mb-6 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-slate-900 mb-1">
                      {selectedUseCase.name}
                    </h3>
                    <p className="text-xs text-slate-600">
                      {selectedUseCase.category} • {selectedUseCase.confidenceLabel} confidence
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedUseCaseId(null)}
                    className="text-xs text-slate-400 hover:text-slate-600"
                  >
                    ×
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-4 text-xs mb-3">
                  <div>
                    <div className="text-slate-500 mb-1">3-yr Net</div>
                    <div className="font-semibold text-slate-900">
                      {formatMillions(selectedUseCase.net3yrValue)}
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500 mb-1">Payback</div>
                    <div className="font-semibold text-slate-900">
                      {selectedUseCase.paybackMonths} mo
                    </div>
                  </div>
                  <div>
                    <div className="text-slate-500 mb-1">Confidence</div>
                    <div className="font-semibold text-slate-900">
                      {selectedUseCase.confidenceLabel}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                    onClick={() => navigate(`/pro/analysis/${selectedUseCase.useCaseId}`)}
                  >
                    Open deep dive →
                  </button>
                </div>
              </section>
            );
          })()}

          {/* Content - Summary Cards */}
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Total 3-Year Net Value
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {formatCurrencyShort(portfolioTotals.totalNet3yr)}
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Total Annual Benefit
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {formatCurrencyShort(portfolioTotals.totalAnnual)}
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Average Payback
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {portfolioTotals.avgPayback
                  ? `${Math.round(portfolioTotals.avgPayback)} months`
                  : '—'}
              </p>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Portfolio Confidence
              </p>
              <p className="mt-2 text-2xl font-semibold text-slate-900">
                {portfolioTotals.avgConfidence}
              </p>
            </div>
          </section>

          {/* Use Case Table */}
          <section className="mt-8 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-slate-900">
                Use Cases · Sorted by 3-Year Net Value
              </h2>
              <p className="text-xs text-slate-500">
                {sortedSummaries.length} opportunities
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Use Case
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Category
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      3-Yr Net Value
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Annual Benefit
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      ROI (3-Yr)
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Payback
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Confidence
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Decision
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {sortedSummaries.map((uc) => {
                    const rowStatus = statusByUseCaseId.get(uc.useCaseId);
                    const currentDecision = rowStatus?.decision || 'undecided';
                    const isSelected = selectedUseCaseId === uc.useCaseId;

                    return (
                      <tr
                        key={uc.useCaseId}
                        onClick={() => setSelectedUseCaseId(uc.useCaseId)}
                        className={cn(
                          'cursor-pointer hover:bg-slate-50 transition-colors',
                          isSelected && 'bg-slate-50'
                        )}
                      >
                        <td className="px-6 py-4 text-slate-900 font-medium">
                          {uc.name}
                        </td>
                        <td className="px-6 py-4 text-slate-600">
                          {uc.category}
                        </td>
                        <td className="px-6 py-4 text-right text-slate-900 font-medium">
                          {formatCurrencyShort(uc.net3yrValue)}
                        </td>
                        <td className="px-6 py-4 text-right text-slate-600">
                          {formatCurrencyShort(uc.annualBenefit)}
                        </td>
                        <td className="px-6 py-4 text-right text-slate-600">
                          {formatPercent(uc.roiPercent)}
                        </td>
                        <td className="px-6 py-4 text-right text-slate-600">
                          {uc.paybackMonths} mo
                        </td>
                        <td className="px-6 py-4 text-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getConfidenceBadgeColor(
                              uc.confidenceLabel
                            )}`}
                          >
                            {uc.confidenceLabel}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center">
                          <select
                            className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white hover:border-slate-300 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                            value={currentDecision}
                            onChange={(e) => {
                              e.stopPropagation(); // Prevent row click
                              handleDecisionChange(uc.useCaseId, e.target.value as DecisionValue);
                            }}
                            onClick={(e) => e.stopPropagation()} // Prevent row click when clicking select
                          >
                            {DECISION_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                {opt === 'undecided'
                                  ? 'Undecided'
                                  : opt.charAt(0).toUpperCase() + opt.slice(1)}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                handleDeepDive(uc.useCaseId);
                              }}
                              className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                            >
                              Deep dive →
                            </button>
                            <button
                              type="button"
                              disabled={currentDecision !== 'proceed'}
                              onClick={(e) => {
                                e.stopPropagation(); // Prevent row click
                                if (currentDecision === 'proceed') {
                                  handleSendToApproval(uc.useCaseId);
                                }
                              }}
                              className={cn(
                                'text-xs font-medium rounded-full px-3 py-1 border transition-colors',
                                currentDecision === 'proceed'
                                  ? 'border-indigo-500 text-indigo-700 hover:bg-indigo-50 cursor-pointer'
                                  : 'border-slate-200 text-slate-400 cursor-not-allowed'
                              )}
                            >
                              Send to Approval
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}

                  {sortedSummaries.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-6 py-12 text-center text-sm text-slate-500"
                      >
                        No ROI projections available yet. Complete personalization
                        and run ROI modeling to see financial impact.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>

        {/* RIGHT: Prospect Panel - Fixed */}
        {clientId && (
          <div className="hidden lg:block">
            <ProspectPanel
              prospects={prospects}
              onRemove={handleProspectRemove}
              onProfileClick={handleProfileClick}
              onRoiClick={handleRoiClick}
              onPlanClick={handlePlanClick}
              onReorder={handleReorder}
            />
          </div>
        )}
      </div>
    </main>
  );
};

export default ROIModelingPage;